import { useEffect, useMemo, useState } from "preact/hooks";

interface PaginationOptions {
  pageSize?: number;
  resetKey?: string | number;
}

export function usePagination<T>(
  items: T[],
  { pageSize = 20, resetKey }: PaginationOptions = {},
) {
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [resetKey, pageSize]);

  const totalItems = items.length;
  const pageCount = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.min(page, pageCount);

  useEffect(() => {
    if (page !== safePage) setPage(safePage);
  }, [page, safePage]);

  const pageItems = useMemo(() => {
    const startIndex = (safePage - 1) * pageSize;
    return items.slice(startIndex, startIndex + pageSize);
  }, [items, pageSize, safePage]);

  const rangeStart = totalItems ? (safePage - 1) * pageSize + 1 : 0;
  const rangeEnd = Math.min(totalItems, safePage * pageSize);

  return {
    page: safePage,
    pageCount,
    pageItems,
    pageSize,
    rangeStart,
    rangeEnd,
    totalItems,
    setPage,
  };
}

interface PaginationProps {
  page: number;
  pageCount: number;
  rangeStart: number;
  rangeEnd: number;
  totalItems: number;
  onPageChange: (page: number) => void;
}

export function Pagination(
  { page, pageCount, rangeStart, rangeEnd, totalItems, onPageChange }: PaginationProps,
) {
  if (pageCount <= 1) return null;
  return (
    <nav class="pagination" aria-label="Paginação">
      <span class="pagination-summary">
        {rangeStart}-{rangeEnd} de {totalItems}
      </span>
      <span class="pagination-actions">
        <button
          class="btn"
          type="button"
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          Anterior
        </button>
        <span class="pagination-current">
          {page} / {pageCount}
        </span>
        <button
          class="btn"
          type="button"
          disabled={page >= pageCount}
          onClick={() => onPageChange(page + 1)}
        >
          Próxima
        </button>
      </span>
    </nav>
  );
}
